import React, { useState } from 'react';
import api from '../services/api';
import CommentSection from './CommentSection';
import '../styles/PostCard.css';

const URL_REGEX = /https?:\/\/[^\s<>"']+/gi;

function renderContent(text) {
  const parts = [];
  let lastIndex = 0;
  let match;
  const regex = new RegExp(URL_REGEX.source, 'gi');

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    parts.push(
      <a key={match.index} href={match[0]} target="_blank" rel="noopener noreferrer">
        {match[0]}
      </a>
    );
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

function ImageGrid({ images }) {
  if (!images.length) return null;
  const count = Math.min(images.length, 4);
  const extra = images.length - 4;

  return (
    <div className={`post-image-grid grid-count-${count}`}>
      {images.slice(0, 4).map((url, i) => (
        <div key={i} className="post-image-cell">
          <img src={url} alt={`Post ${i + 1}`} />
          {i === 3 && extra > 0 && (
            <div className="post-image-overflow">+{extra}</div>
          )}
        </div>
      ))}
    </div>
  );
}

const REACTIONS = [
  { type: 'thumbs_up', emoji: '👍' },
  { type: 'thumbs_down', emoji: '👎' },
  { type: 'lol', emoji: '😂' },
  { type: 'heart', emoji: '❤️' },
  { type: 'exclamation', emoji: '❗' },
];

function ReactionBar({ postId, initialReactions }) {
  const init = initialReactions || {};
  const [counts, setCounts] = useState({
    thumbs_up: init.thumbs_up || 0,
    thumbs_down: init.thumbs_down || 0,
    lol: init.lol || 0,
    heart: init.heart || 0,
    exclamation: init.exclamation || 0,
  });
  const [userReaction, setUserReaction] = useState(init.user_reaction || null);

  const handleReaction = async (type) => {
    const prevReaction = userReaction;
    const prevCounts = { ...counts };

    const next = { ...counts };
    if (prevReaction === type) {
      next[type]--;
      setUserReaction(null);
    } else {
      if (prevReaction) next[prevReaction]--;
      next[type]++;
      setUserReaction(type);
    }
    setCounts(next);

    try {
      await api.post('/reactions/', { post_id: postId, reaction_type: type });
    } catch {
      setCounts(prevCounts);
      setUserReaction(prevReaction);
    }
  };

  return (
    <div className="reaction-bar">
      {REACTIONS.map(({ type, emoji }) => (
        <button
          key={type}
          className={`reaction-btn${userReaction === type ? ' active' : ''}`}
          onClick={() => handleReaction(type)}
          title={type.replace('_', ' ')}
        >
          <span className="reaction-emoji">{emoji}</span>
          {counts[type] > 0 && <span className="reaction-count">{counts[type]}</span>}
        </button>
      ))}
    </div>
  );
}

function PostCard({ post, currentUserId, onDelete }) {
  const isOwnPost = post.author_id === currentUserId;
  const authorName = post.profiles?.full_name || 'Unknown User';

  const images = post.image_urls?.length > 0
    ? post.image_urls
    : (post.image_url ? [post.image_url] : []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${Math.floor(diffInHours)}h ago`;
    if (diffInHours < 48) return 'Yesterday';
    return date.toLocaleDateString();
  };

  return (
    <div className="post-card">
      <div className="post-header">
        <div className="post-author">
          <div className="author-avatar">
            {post.profiles?.avatar_url
              ? <img src={post.profiles.avatar_url} alt={authorName} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
              : authorName.charAt(0).toUpperCase()
            }
          </div>
          <div className="author-info">
            <h3>{authorName}</h3>
            <span className="post-time">{formatDate(post.created_at)}</span>
          </div>
        </div>

        {isOwnPost && (
          <button onClick={() => onDelete(post.id)} className="delete-btn" title="Delete post">×</button>
        )}
      </div>

      {post.content && (
        <div className="post-content">
          <p>{renderContent(post.content)}</p>
        </div>
      )}

      <ImageGrid images={images} />

      {post.link_preview && (
        <a href={post.link_preview.url} target="_blank" rel="noopener noreferrer" className="link-preview-card">
          {post.link_preview.image && (
            <img src={post.link_preview.image} alt="" className="link-preview-card-img" />
          )}
          <div className="link-preview-card-body">
            {post.link_preview.title && <p className="link-preview-card-title">{post.link_preview.title}</p>}
            {post.link_preview.description && <p className="link-preview-card-desc">{post.link_preview.description}</p>}
            <p className="link-preview-card-domain">{post.link_preview.domain}</p>
          </div>
        </a>
      )}

      <ReactionBar postId={post.id} initialReactions={post.reactions} />

      <CommentSection postId={post.id} currentUserId={currentUserId} />
    </div>
  );
}

export default PostCard;
